/**
 * Use helper class to process payments from the element (or any other source)
 * 'stripe' and 'elements' must be used within the context of the StripeProvider
 * Separated out to clean up Summary.tsx
 */

import { Stripe, StripeElements } from '@stripe/stripe-js';

import {
  STEP,
  GLOBAL_ERROR_NAME,
  CHECKOUT_ERRORS,
  STRIPE_HELPER_KEYS,
} from '~/constants/checkout';
import { CartProps } from '~/types/cart';
import {
  BillingDetailsProps,
  CreateStripePaymentMethodProps,
  HandlePaymentResponseProps,
  CreateStripeCardTokenResponseProps,
} from '~/types/checkout';
import { fetchJson } from '~/lib/fetch';

export default class StripeHelper {
  stripe: Stripe | null = null;
  elements: StripeElements | null = null;
  cart: CartProps | null = null;
  pendingNextAction: any = null;

  constructor(
    stripe: Stripe | null,
    elements: StripeElements | null,
    cart: CartProps | null,
  ) {
    this.stripe = stripe;
    this.elements = elements;
    this.cart = cart;
  }

  // Using any here so we can use one fuction to set the values when they change
  public setValue(key: STRIPE_HELPER_KEYS, value: any) {
    if (key === STRIPE_HELPER_KEYS.stripe) this.stripe = value;
    if (key === STRIPE_HELPER_KEYS.elements) this.elements = value;
    if (key === STRIPE_HELPER_KEYS.cart) this.cart = value;
  }

  public async createStripePaymentMethod(
    billingDetails: BillingDetailsProps,
  ): CreateStripePaymentMethodProps {
    if (!this.stripe || !this.elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return {
        error: CHECKOUT_ERRORS.STRIPE_NOT_LOADED,
        paymentMethod: undefined,
      };
    }

    // Trigger form validation and wallet collection
    const { error: submitError } = await this.elements.submit();

    if (submitError) {
      return {
        error: submitError?.message || CHECKOUT_ERRORS.STRIPE_SUBMIT,
        paymentMethod: undefined,
      };
    }

    // Create the PaymentMethod using the details collected by the Payment Element
    const { error, paymentMethod } = await this.stripe.createPaymentMethod({
      elements: this.elements,
      params: {
        billing_details: {
          name: `${this.cart?.first_name || ''} ${this.cart?.last_name || ''}`,
          address: {
            country: `${billingDetails?.country?.value || ''}`, // Typescript value could be a number
            postal_code: billingDetails.postalCode || '',
          },
        },
      },
    });

    return {
      error: error?.message || CHECKOUT_ERRORS.STRIPE_SUBMIT,
      paymentMethod,
    };
  }

  public async handleServerResponse(response: any): HandlePaymentResponseProps {
    let errorMessage = CHECKOUT_ERRORS.GENERIC;
    let step = GLOBAL_ERROR_NAME;
    let returnUrl = '';

    if (!this.stripe) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return { errorMessage, step, returnUrl };
    }

    if (response.error) {
      // Show error from server on payment form
    } else if (response.status === 'requires_action') {
      // Use Stripe.js to handle the required next action
      const { error, paymentIntent } = await this.stripe.handleNextAction({
        clientSecret: response.client_secret,
      });

      if (
        paymentIntent?.status === 'succeeded' &&
        response?.type === 'use_stripe_sdk'
      ) {
        try {
          const subscriptionNextAction = await fetchJson(
            '/api/3ds-confirmation/',
            {
              method: 'POST',
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
              },
              include: 'credentials',
              body: JSON.stringify({ invoice_uuid: response?.invoice_uuid }),
            },
          );

          return this.handleServerResponse(subscriptionNextAction);
        } catch (error) {
          errorMessage = CHECKOUT_ERRORS.PAYMENT_METHOD;
          return { errorMessage, step, returnUrl };
        }
      } else if (error) {
        errorMessage = error.message || CHECKOUT_ERRORS.STRIPE_REDIRECT;
      } else if (paymentIntent?.last_payment_error?.message) {
        errorMessage =
          paymentIntent.last_payment_error.message ||
          CHECKOUT_ERRORS.STRIPE_REDIRECT;
        step = STEP.PAYMENT;
      } else if (paymentIntent?.status === 'requires_payment_method') {
        errorMessage = CHECKOUT_ERRORS.PAYMENT;
        step = STEP.PAYMENT;
      } else {
        errorMessage = CHECKOUT_ERRORS.STRIPE_SUBMIT;
        step = STEP.PAYMENT;
      }
    } else if (response.success && response.success_url) {
      returnUrl =
        (process.env.NEXT_PUBLIC_DJANGO_PUBLIC_URL || '') +
        response.success_url;
      errorMessage = '';
      step = '';
    }

    return { errorMessage, step, returnUrl };
  }

  public async createStripeCardToken(): Promise<CreateStripeCardTokenResponseProps> {
    if (!this.stripe || !this.elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return { error: CHECKOUT_ERRORS.STRIPE_NOT_LOADED };
    }

    // Trigger form validation
    const { error: submitError } = await this.elements.submit();
    if (submitError) return { error: submitError.message };
    // Retrieve the card information
    const cardElement = this.elements.getElement('card');
    if (!cardElement) return { error: 'Card element not found' };
    // Retrieve the address information
    const addressElement = this.elements.getElement('address');
    if (!addressElement) return { error: 'Address element not found' };
    const {
      value: { name, address },
    } = await addressElement.getValue();

    const { error, token } = await this.stripe.createToken(cardElement, {
      name: name,
      address_city: address.city,
      address_country: address.country,
      address_line1: address.line1,
      address_line2: address.line2 || undefined,
      address_state: address.state,
      address_zip: address.postal_code,
    });

    if (error) {
      // Show error from server on payment form
      return { error: error.message };
    }

    return { tokenId: token?.id };
  }
}
